/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import { generate } from 'astring';
import { valueToEstree } from 'estree-util-value-to-estree';

// @ts-check

// TODO: Zvážit, zda kvůli nemalým závislostem astring a estree-util-value-to-estree
// nepovolit jen v testech (tj. soubor přejmenovat na stringify.unit.js a refaktorovat
// stávající výskyty v produkčním kódu).
/**
 * Pomocné funkce pro serializaci hodnot a výrazů ve formátu zdrojového kódu JS.
 * Např. {@link value} je něco jako JSON.stringify, akorát podporuje i undefined.
 */
export default class Stringify {
  // TODO: Předělat fnName na obecnější typ string | number | symbol a správně
  // tyto případy serializovat.
  /**
   * Serializuje výraz volání funkce, tj. jméno funkce a její argumenty.
   *
   * Pozor na to, že vzhledem k použití a omezením {@link value} neumí
   * rekurzivně serializovat všechny typy argumentů (např. funkce/callbacky,
   * atd.), takže je potřeba nevhodné (pod)argumenty před voláním omitovat.
   * @param {string} fnName
   * @param  {...any} args
   * @returns {string}
   * @throws {Error} Pokud nelze dané argumenty serializovat.
   */
  static call(fnName, ...args) {
    /** @type {import('estree').CallExpression} */
    const node = {
      type: 'CallExpression',
      optional: false,
      callee: /** @type {import('estree').Identifier} */ ({
        type: 'Identifier',
        name: fnName
      }),
      arguments: args.map(nodeFromValue)
    };
    return generateSingleLine(node);
  }

  // Commit se zavedením vyhazování výjimky:
  // - https://github.com/remcohaszing/estree-util-value-to-estree/commit/1c02c714688dddeb3fca93127b393ccf4643a8b2
  /**
   * Serializuje JS hodnotu jako JS řetězec. Podporuje JSON množinu typů, navíc
   * undefined, nějaké známé objekty a neznámé serializuje jako plain objekty.
   * Pozor, Nepodporuje pokročilé věci, jako např. funkce a non-plain objekty,
   * a vyhodí výjimku, i na zanořené vnitřní hodnoty!
   * @param {*} v
   * @returns {string}
   * @throws {TypeError} Pokud nelze dané argumenty serializovat.
   * Typicky se jedná o non-plain objekty (třídy) a funkce.
   */
  static value(v) {
    return generateSingleLine(nodeFromValue(v));
  }

  /**
   * Safe varianta Stringify.value s fallbackem na String konstruktor
   * a JSON.stringify (s fallbackem při cyklických referencí), který ale neumí
   * serializovat např. undefined a neprimitivní hodnoty.
   *
   * Může vrátit neformální výsup, který není validní JS/JSON hodnotou.
   * @param {any} v
   * @returns {string}
   */
  static valueSafeInformal(v) {
    try {
      return Stringify.value(v);
    } catch {
      let json;
      // JSON.stringify může vyhodit při cyklických referencích.
      try {
        json = `(incomplete JSON: ${JSON.stringify(v)})`;
      } catch {
        json = '(circular structure)';
      }
      return `${String(v)} ${json}`;
    }
  }
}

/**
 * @param {import('estree').Node} node
 * @returns {string}
 */
function generateSingleLine(node) {
  return generate(node, { indent: '', lineEnd: ' ' });
}

/**
 * @param {*} v
 * @returns {import('estree').Expression}
 * @throws {Error} Pokud nelze dané argumenty serializovat.
 */
function nodeFromValue(v) {
  return valueToEstree(v, { instanceAsObject: true });
}
